<template>
  <main :class="[routeName, 'c-base-view']" :data-theme="switchTheme">
    <Hero :data="dataHero"/>

    <template v-if="routeName !== 'speakers'">
      <BaseSection
        v-for="(component, key) in trimmedStructure"
        :key="key"
        :data="component.data"
        :component="getComponentName(component.component)"
      />
    </template>

    <SpeakersContainer :translate="translate" :data="trimmedStructure" v-else/>
  </main>
</template>

<script>
import { mapGetters } from 'vuex';

import Hero from '@/components/Hero.vue';
import BaseSection from '@/components/BaseSection.vue';
import SpeakersContainer from '@/components/SpeakersContainer.vue';

import loop from '@/scripts/loop';
import initialState from '@/scripts/initialState';
import animations from '@/scripts/animations';
import { qs, qsa, sortObject, isMobile } from '@/scripts/utils';


export default {
  name: 'baseView',

  components: {
    Hero,
    BaseSection,
    SpeakersContainer,
  },

  props: {
    translate: {
      type: Number,
      required: false,
    },
  },

  data() {
    return {
      elsToAnimate: [],
      elsParallax: [],
      loopIsRemoved: false,
      route: '',

      theme: false,

      distanceEnter: 150,
    };
  },

  computed: {
    ...mapGetters({
      loaderState: 'getStateLoader',

      city: 'getCity',
      routeName: 'getRouteName',

      getStructure: 'getStructure',
      components: 'getComponents',
    }),

    structure() {
      return this.getStructure(this.city, this.routeName);
    },

    trimmedStructure() {
      const structure = {};

      Object.keys(this.structure).forEach((key) => {
        if (this.structure[key].order !== 0) structure[key] = this.structure[key];
      });

      return sortObject(structure, 'order');
    },

    dataHero() {
      const { structure } = this;
      let found = null;

      Object.keys(structure).forEach((key) => {
        if (structure[key].order === 0) found = structure[key];
      });

      return found.data;
    },

    switchTheme() {
      switch (true) {
        case this.routeName === 'speakers':
          this.theme = 'red';
          break;

        case this.routeName === 'schedule':
          this.theme = 'gray';
          break;

        case this.routeName === 'workshops':
          this.theme = this.city === 'new-york' ? 'blue' : 'azure';
          break;

        case this.routeName === 'venues':
          this.theme = 'yellow';
          break;

        case this.routeName === 'adobe-meetup':
          this.theme = 'pink';
          break;

        default:
          this.theme = false;
          break;
      }

      return this.theme;
    },
  },

  mounted() {
    this.route = this.routeName;

    this.init();

    if (this.elsToAnimate.length !== 0) loop.add(this.checkPosition, `set-${this.route}-animation`, true);
    if (this.elsParallax.length !== 0) loop.add(this.checkParallax, `set-${this.route}-parallax`);
    if (!isMobile()) loop.add(this.checkScrollDown, 'checkScrollDown');

    this.$nextTick(() => {
      this.$eventHub.$emit('domRendered', { val: true, height: this.$el.getBoundingClientRect().height });

      this.resize();
    });
  },

  methods: {
    init() {
      const els = qsa('[js-anim]', this.$el);
      const elsParallax = qsa('[js-parallax]', this.$el);

      if (els.length !== 0) {
        els.forEach((el) => {
          el.style.opacity = 0; // eslint-disable-line
          initialState.get(el.getAttribute('js-anim'))(el, this.elsToAnimate);
        });
      }

      if (elsParallax.length !== 0) {
        elsParallax.forEach((el) => {
          const offset = el.getAttribute('js-parallax') !== '' ? el.getAttribute('js-parallax') : 0.2;

          this.elsParallax.push({ node: el, visible: false, offset });
        });
      }

      this.distanceEnter = this.routeName === 'speakers' ? 75 : 150;
    },

    resize() {
      if (this.elsToAnimate.length !== 0) {
        this.elsToAnimate.forEach((el) => {
          const limit = el.node.getBoundingClientRect().top;

          el.fromTop = el.node.getAttribute('js-anim') === 'picture' || el.node.getAttribute('js-anim') === 'herotext' ? limit - 200 : limit; // eslint-disable-line
        });
      }

      if (this.elsParallax.length !== 0) {
        this.elsParallax.forEach((el) => {
          el.fromTop = el.node.getBoundingClientRect().top; // eslint-disable-line
          el.fromBottom = el.node.getBoundingClientRect().top + el.node.getBoundingClientRect().height + window.innerHeight; // eslint-disable-line
        });
      }
    },

    checkPosition() {
      const scrollPos = (window.lerpedScrollY + (window.innerHeight - this.distanceEnter));

      this.elsToAnimate.forEach((el, i) => {
        if (scrollPos > el.fromTop && !el.node.hasAttribute('js-animated') && !this.loaderState) {
          el.node.setAttribute('js-animated', '');

          animations.get(el.node.getAttribute('js-anim'))(el);

          if (this.elsToAnimate.length === 1) {
            loop.remove(`set-${this.route}-animation`);
            this.loopIsRemoved = true;
          }

          this.elsToAnimate.splice(i, 1);
          el.node.removeAttribute('js-animated');
        }
      });
    },

    checkParallax() {
      const scrollPos = (window.lerpedScrollY + window.innerHeight);

      this.elsParallax.forEach((el) => {
        if (scrollPos > el.fromTop && el.fromBottom > scrollPos && !this.loaderState) {
          animations.get('parallax')(el, scrollPos);
        }
      });
    },

    checkScrollDown() {
      const scrollPos = (window.lerpedScrollY + window.innerHeight);

      if (scrollPos > window.innerHeight + 150) {
        qs('[js-scroll]').classList.add('fade-out');
        loop.remove('checkScrollDown');
      }
    },

    getComponentName(component) {
      return this.components[component].component;
    },
  },

  beforeDestroy() {
    this.$eventHub.$emit('domRendered', { val: false, height: 0 });

    if (!this.loopIsRemoved) loop.remove(`set-${this.route}-animation`);
    loop.remove(`set-${this.route}-parallax`);
    if (!isMobile()) loop.remove('checkScrollDown');
  },
};
</script>

<style lang="scss" scoped>
main {
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);

  @at-root [data-page="adobe-meetup"] {
    main[data-theme="pink"] {
      .workshopsitem {
        padding-bottom: 0;
      }

      .showcase + .showcase {
        padding-top: 0;
      }
    }
  }
}
</style>

